import React from 'react';
import {
  ShieldAlert,
  Gavel,
  UserX,
  Lock,
  History,
  ArrowRight,
  AlertTriangle,
  Zap,
  Building2,
  Code2,
  Check,
  Bitcoin,
} from 'lucide-react';

import {
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';

function RiskCard({ icon, title, children, color = 'red' }) {
  return (
    <Paper withBorder p="xl" radius="md" h="100%">
      <Stack gap="md">
        <ThemeIcon color={color} variant="light" size={48} radius="md">
          {icon}
        </ThemeIcon>
        <Title order={3} fz="xl">
          {title}
        </Title>
        <Text size="sm" c="dimmed" lh={1.6}>
          {children}
        </Text>
      </Stack>
    </Paper>
  );
}

function AudienceCard({ icon, title, items, color = 'blue' }) {
  return (
    <Paper withBorder p="xl" radius="md" h="100%">
      <Stack gap="md">
        <Group gap="sm">
          <ThemeIcon variant="light" color={color} radius="xl">
            {icon}
          </ThemeIcon>
          <Title order={3} fz="lg">
            {title}
          </Title>
        </Group>

        <List
          spacing="sm"
          size="sm"
          icon={
            <ThemeIcon size={16} radius="xl" variant="light" color={color}>
              <Check size={10} />
            </ThemeIcon>
          }
        >
          {items.map((item) => (
            <List.Item key={item}>
              <Text c="dimmed">{item}</Text>
            </List.Item>
          ))}
        </List>
      </Stack>
    </Paper>
  );
}

export default function Motivations() {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const [, setLocation] = useLocation();

  const navigateInternal = (path) => (event) => {
    event.preventDefault();
    setLocation(path);
  };

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        <Stack gap={80}>
          <Stack align="center" ta="center" pt={40} gap="sm">
            <Badge color="red" variant="light" size="lg" leftSection={<ShieldAlert size={14} />}>
              {t('Critical IP Risks')}
            </Badge>
            <Title order={1} fz={{ base: 36, md: 48 }} fw={900}>
              {t('Why Proving "When" Is Just As')}
              <br />
              {t('Important As "What"')}
            </Title>
            <Text c="dimmed" size="lg" maw={800}>
              {t("In the modern digital economy, your ideas are your most valuable assets. But without a mathematical timestamp, you don't own them-you're just holding them for someone else.")}
            </Text>
            <Text c="dimmed" size="md" maw={800}>
              {t('OpenTimestamps and Bitcoin make blockchain timestamp verification automatic. This is how to prove code existed at a specific time, without revealing the code.')}
            </Text>
            <Badge color="teal" variant="light" size="lg" leftSection={<Bitcoin size={14} />}>
              {t('Your cryptographic proofs are embedded in the Bitcoin blockchain.')}
            </Badge>
          </Stack>

          <Stack gap="xl">
            <Stack gap="xs">
              <Title order={2} fz={{ base: 28, md: 36 }} fw={800}>
                {t('The Vulnerability of "New"')}
              </Title>
              <Text c="dimmed" size="lg">
                {t('Software development moves at light speed, creating three critical legal and strategic blindspots.')}
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
              <RiskCard
                icon={<Gavel size={24} />}
                title={t('The Patent Troll Threat')}
              >
                {t('Non-practicing entities (trolls) often file broad patents on "obvious" technologies. If you implemented that tech months before their filing but have no proof, you face ruinous litigation.')}{' '}
                <Text component="span" fw={700}>
                  {t('Cryptographic prior art')}
                </Text>{' '}
                {t('is your primary shield to invalidate these claims.')}
              </RiskCard>

              <RiskCard
                icon={<UserX size={24} />}
                title={t('Former Employee Disputes')}
              >
                {t('When a lead developer leaves, disputes often arise over when a specific feature or algorithm was conceived. Without an immutable timestamp, it becomes a "he-said, she-said" battle in court.')}
              </RiskCard>

              <RiskCard
                icon={<AlertTriangle size={24} />}
                title={t('The "Clean Room" Fallacy')}
              >
                {t('Competitors often claim they "independently developed" a similar feature. Proving you had the finished logic in your repository 18 months prior creates a mathematical wall they cannot climb.')}
              </RiskCard>

              <RiskCard
                icon={<History size={24} />}
                color="blue"
                title={t('The Fragility of Logs')}
              >
                {t('Standard Git history is stored on servers you do not own (GitHub) or databases you can edit (Local). In a serious legal audit, internal logs are often dismissed as "self-serving" or "manipulatable."')}
              </RiskCard>
            </SimpleGrid>
          </Stack>

          <Paper withBorder p="xl" radius="xl" bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}>
            <Stack gap="xl" align="center" ta="center">
              <Box>
                <Title order={2} mb="sm">
                  {t('The Proof of Existence Gap')}
                </Title>
                <Text c="dimmed" maw={700}>
                  {t('Traditional IP protection is broken. It is either too expensive (Patents) or too weak (Internal Logs). Timestamp GIT creates a new mathematical middle ground.')}
                </Text>
              </Box>

              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" w="100%">
                <Paper withBorder p="lg" radius="md">
                  <Text tt="uppercase" fw={800} fz="xs" c="dimmed" mb={4}>
                    {t('Internal Git Logs')}
                  </Text>
                  <Text fw={700} c="red">
                    {t('Manipulatable')}
                  </Text>
                </Paper>

                <Group justify="center" align="center">
                  <Title order={3}>{t('VS')}</Title>
                </Group>

                <Paper withBorder p="lg" radius="md">
                  <Text tt="uppercase" fw={800} fz="xs" c="dimmed" mb={4}>
                    {t('Timestamp GIT')}
                  </Text>
                  <Text fw={700} c="teal">
                    {t('Mathematically Absolute')}
                  </Text>
                  <Text fz="xs" c="dimmed" mt={4}>
                    {t('Your cryptographic proofs are embedded in the Bitcoin blockchain.')}
                  </Text>
                </Paper>
              </SimpleGrid>
            </Stack>
          </Paper>

          <Stack gap="xl">
            <Title order={2} fz={{ base: 28, md: 36 }} fw={800}>
              {t('Who is this for?')}
            </Title>
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
              <AudienceCard
                icon={<Zap size={16} />}
                title={t('Startups & Founders')}
                items={[
                  t('Secure "Prior Art" before a patent filing.'),
                  t('Increase company valuation by proving IP maturity.'),
                  t('Protect trade secrets without filing public docs.'),
                ]}
              />

              <AudienceCard
                icon={<Building2 size={16} />}
                title={t('Dev Shops & Agencies')}
                items={[
                  t('Prove work-delivery dates during payment disputes.'),
                  t('Shield the agency from liability if clients are sued.'),
                  t('Professional "Certificate of Authorship" handoffs.'),
                ]}
              />

              <AudienceCard
                icon={<Code2 size={16} />}
                title={t('Freelance Developers')}
                items={[
                  t('Defend reusable library code from being claimed.'),
                  t('Mathematically prove milestone completion times.'),
                  t('Secure portfolio pieces against plagiarism.'),
                ]}
              />
            </SimpleGrid>
          </Stack>

          <Paper withBorder radius="xl" p="xl">
            <Stack gap="xl" align="center" ta="center">
              <Title order={2}>{t("Don't Leave Your IP to Chance")}</Title>
              <Text c="dimmed" maw={600}>
                {t('It takes 60 seconds to connect your first repository. It takes a lifetime to recover from a stolen idea.')}
              </Text>
              <Button
                component="a"
                href="/pricing"
                onClick={navigateInternal('/pricing')}
                color="teal"
                size="lg"
                radius="md"
                rightSection={<ArrowRight size={20} />}
              >
                {t('Start Securing Prior Art')}
              </Button>
              <Group gap="xs" c="dimmed" fz="sm" justify="center">
                <Lock size={14} />
                {t('Zero-Knowledge: We never read code. We only mathematically stamp the hash.')}
              </Group>
            </Stack>
          </Paper>

          <Box component="footer" py={40}>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl" mb={40}>
              <Stack gap="sm">
                <Text fw={700} fz="xs" tt="uppercase" lts={1}>
                  {t('Resources')}
                </Text>
                <Anchor href="#" c="dimmed" fz="sm">
                  {t('Legal Whitepaper')}
                </Anchor>
                <Anchor href="#" c="dimmed" fz="sm">
                  {t('Patent Troll Defense')}
                </Anchor>
                <Anchor href="#" c="dimmed" fz="sm">
                  {t('Audit Guide')}
                </Anchor>
              </Stack>

              <Stack gap="sm">
                <Text fw={700} fz="xs" tt="uppercase" lts={1}>
                  {t('Integrations')}
                </Text>
                <Anchor href="#" c="dimmed" fz="sm">
                  {t('GitHub App')}
                </Anchor>
                <Anchor href="#" c="dimmed" fz="sm">
                  {t('Enterprise Docker')}
                </Anchor>
                <Anchor href="#" c="dimmed" fz="sm">
                  {t('API Reference')}
                </Anchor>
                <Anchor
                  href="https://opentimestamps.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  c="dimmed"
                  fz="sm"
                >
                  {t('opentimestamps.org')}
                </Anchor>
              </Stack>

              <Stack gap="sm">
                <Text fw={700} fz="xs" tt="uppercase" lts={1}>
                  {t('More Products')}
                </Text>
                <Anchor href="https://py2native.dev" target="_blank" rel="noopener noreferrer" c="dimmed" fz="sm">
                  {t('Py2Native')}
                </Anchor>
                <Text c="dimmed" fz="xs">
                  {t('Compile Python to native and protect your source code.')}
                </Text>
              </Stack>

              <Stack gap="sm">
                <Text fw={700} fz="xs" tt="uppercase" lts={1}>
                  {t('Company')}
                </Text>
                <Anchor href="/imprint" onClick={navigateInternal('/imprint')} c="dimmed" fz="sm">
                  {t('Legal Notice')}
                </Anchor>
                <Anchor href="/privacy" onClick={navigateInternal('/privacy')} c="dimmed" fz="sm">
                  {t('Privacy Policy')}
                </Anchor>
                <Anchor href="/terms" onClick={navigateInternal('/terms')} c="dimmed" fz="sm">
                  {t('Terms of Service')}
                </Anchor>
              </Stack>
            </SimpleGrid>

            <Divider color="dark.4" />

            <Group justify="center" pt="xl">
              <Text size="sm" c="dimmed" ta="center">
                {t('(c) 2026 RSJ Software GmbH - Germering, Germany - All Intellectual Property Protected via Bitcoin.')}
              </Text>
            </Group>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
